import React from 'react';

/**
 * NotaryDocumentPreview
 * Specimen anonymisé d'un acte notarié français — utilisé uniquement à des fins illustratives.
 * Aucun nom réel n'est utilisé.
 */
const NotaryDocumentPreview = ({ compact = false }) => {
  const docRotation = -0.4;
  const stampRotation = -9;
  const stampOffsetX = 3;

  return (
    <div className={`relative ${compact ? 'max-w-md' : 'max-w-2xl'} mx-auto px-2`}>
      {/* Outer shadow simulating depth on scan surface */}
      <div
        className="absolute inset-0 rounded-sm"
        style={{
          boxShadow: '0 30px 60px -25px rgba(50, 35, 20, 0.35), 0 12px 25px -8px rgba(50, 35, 20, 0.18)',
          transform: `rotate(${docRotation}deg)`,
        }}
      ></div>

      {/* Main document container */}
      <div
        className="relative bg-[#FDFBF6] rounded-sm border border-[#E2DBCF] overflow-hidden"
        style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          transform: `rotate(${docRotation}deg)`,
          backgroundImage: `
            repeating-linear-gradient(
              0deg,
              transparent,
              transparent 28px,
              rgba(180, 160, 130, 0.025) 28px,
              rgba(180, 160, 130, 0.025) 29px
            )
          `,
        }}
      >
        {/* Paper grain */}
        <div
          className="absolute inset-0 pointer-events-none mix-blend-multiply opacity-[0.35]"
          style={{
            backgroundImage: `
              radial-gradient(circle at 15% 25%, rgba(180, 150, 110, 0.10) 0%, transparent 35%),
              radial-gradient(circle at 85% 75%, rgba(150, 120, 80, 0.08) 0%, transparent 40%),
              radial-gradient(circle at 50% 50%, rgba(200, 180, 150, 0.05) 0%, transparent 60%)
            `,
          }}
        ></div>

        {/* Vignette */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at center, transparent 55%, rgba(80, 60, 30, 0.06) 90%, rgba(50, 35, 15, 0.10) 100%)',
          }}
        ></div>

        {/* Scan artifacts */}
        <div className="absolute pointer-events-none" style={{ top: '11%', right: '9%', width: '2px', height: '2px', background: 'rgba(70, 50, 30, 0.25)', borderRadius: '50%', filter: 'blur(0.5px)' }}></div>
        <div className="absolute pointer-events-none" style={{ top: '42%', left: '7%', width: '1px', height: '8px', background: 'rgba(60, 45, 25, 0.18)', transform: 'rotate(70deg)', filter: 'blur(0.4px)' }}></div>
        <div className="absolute pointer-events-none" style={{ bottom: '30%', right: '13%', width: '14px', height: '8px', background: 'radial-gradient(ellipse, rgba(100, 80, 50, 0.08) 0%, transparent 70%)' }}></div>

        <div className={`relative ${compact ? 'p-6 sm:p-7' : 'p-8 sm:p-10'}`}>
          {/* Letterhead */}
          <div className="text-center mb-5 pb-4 border-b border-[#1A2530]/20">
            <p className={`${compact ? 'text-[9px]' : 'text-[10px] sm:text-xs'} tracking-[0.3em] text-[#1A2530] mb-2 font-medium`} style={{ opacity: 0.92 }}>
              RÉPUBLIQUE FRANÇAISE
            </p>
            <h3 className={`${compact ? 'text-[15px]' : 'text-base sm:text-lg'} text-[#1A2530] tracking-wider`} style={{ fontVariant: 'small-caps', fontWeight: 600 }}>
              Office Notarial — Spécimen
            </h3>
            <p className={`${compact ? 'text-[10px]' : 'text-[11px]'} text-[#2C363F] mt-1 italic`}>
              Document anonymisé à titre d'illustration
            </p>
            <p className={`${compact ? 'text-[8px]' : 'text-[9px] sm:text-[10px]'} text-[#2C363F] mt-0.5`} style={{ opacity: 0.78 }}>
              Spécimen — aucune référence réelle
            </p>
          </div>

          {/* Document Title */}
          <div className="text-center mb-5">
            <p className={`${compact ? 'text-[10px]' : 'text-[11px] sm:text-xs'} tracking-[0.25em] text-[#2C363F] mb-1`}>
              ACTE DE NOTORIÉTÉ ET DÉVOLUTION SUCCESSORALE
            </p>
            <p className={`${compact ? 'text-[9px]' : 'text-[10px]'} text-[#2C363F] italic`} style={{ opacity: 0.82 }}>
              Spécimen anonymisé — ne reflète aucun dossier réel
            </p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <div className="h-px w-10 bg-[#1A2530]/30"></div>
              <span className={`${compact ? 'text-[9px]' : 'text-[10px]'} text-[#2C363F] tracking-wider`}>※</span>
              <div className="h-px w-10 bg-[#1A2530]/30"></div>
            </div>
          </div>

          {/* Body */}
          <div className={`${compact ? 'text-[10.5px] leading-[1.7]' : 'text-[11.5px] sm:text-[12px] leading-[1.85]'} text-[#1A2530] text-justify space-y-3`} style={{ letterSpacing: '0.01em' }}>
            <p className="first-letter:text-3xl first-letter:font-semibold first-letter:mr-1 first-letter:float-left first-letter:leading-[0.85] first-letter:mt-1">
              <span style={{ fontVariant: 'small-caps', fontWeight: 600 }}>Par-devant</span>{' '}
              Maître X…, notaire associé(e), ont comparu les héritiers de feu Madame Y…, en présence du conjoint survivant.
            </p>

            <p>
              <span className="font-semibold tracking-wider">CONSTATATIONS —</span> Il résulte d'un acte de décès dressé par les services de l'état civil que Madame Y…, née le […], demeurant en dernier lieu à […], est décédée à son domicile le […], à l'âge de quatre-vingt-un ans.
            </p>

            <p>
              <span className="font-semibold tracking-wider">DÉVOLUTION —</span> Madame Y…, veuve en premières noces et remariée en secondes noces à Monsieur Z…, laisse pour lui succéder :
            </p>

            <div className="pl-6 space-y-1" style={{ fontSize: '0.97em' }}>
              <p>1°/ Ses trois enfants issus de sa première union, à savoir :</p>
              <p className="pl-4">— Madame{' '}<span className="px-1" style={{ background: 'rgba(253, 246, 216, 0.7)' }}>A…</span>, sa fille aînée ;</p>
              <p className="pl-4">— Monsieur B…, son fils ;</p>
              <p className="pl-4">— Madame C…, sa fille cadette ;</p>
              <p>2°/ Son conjoint survivant, Monsieur Z….</p>
            </div>

            <p>
              <span className="font-semibold tracking-wider">OPTION DU CONJOINT —</span> En l'absence de testament ou de donation entre époux, et en présence d'enfants non issus du second mariage, Monsieur Z… a, conformément à l'article 757 du Code civil, opté pour <span className="italic">l'usufruit de la totalité</span> de la succession. Les trois enfants recueillent en conséquence chacun un <span className="italic">tiers en nue-propriété</span> indivise.
            </p>

            {!compact && (
              <p className="pt-1">
                <span className="font-semibold tracking-wider">DONT ACTE</span> dressé et passé en l'étude, lecture faite et les parties ayant persisté, ont signé avec le notaire soussigné.
              </p>
            )}
          </div>

          {/* Signatures and stamp */}
          <div className={`${compact ? 'mt-5 pt-4' : 'mt-7 pt-5'} border-t border-[#1A2530]/15 flex items-end justify-between gap-4`}>
            <div className="flex-1 min-w-0">
              <p className={`${compact ? 'text-[9px]' : 'text-[10px]'} text-[#2C363F] mb-2 tracking-wider`}>
                Les comparants
              </p>
              <div className="relative h-10">
                <svg className="absolute left-0 top-0" width={compact ? '78' : '95'} height={compact ? '32' : '38'} viewBox="0 0 100 40" fill="none" style={{ opacity: 0.88 }}>
                  <path d="M 4,29 Q 11,11 17,23 Q 23,32 30,17 Q 37,7 45,25 Q 51,33 61,15 Q 69,9 79,21 L 91,19" stroke="#1A3A5C" strokeWidth="1.3" fill="none" strokeLinecap="round" />
                  <path d="M 77,29 Q 83,22 90,30" stroke="#1A3A5C" strokeWidth="1" fill="none" strokeLinecap="round" />
                  <circle cx="92" cy="20" r="0.8" fill="#1A3A5C" opacity="0.7" />
                </svg>
              </div>
            </div>

            {/* Stamp */}
            <div className="flex-shrink-0 flex flex-col items-center">
              <div
                className={`${compact ? 'w-[78px] h-[78px]' : 'w-[92px] h-[92px]'} rounded-full flex items-center justify-center relative`}
                style={{
                  border: '1.5px solid #1A3A5C',
                  transform: `rotate(${stampRotation}deg) translateX(${stampOffsetX}px)`,
                  opacity: 0.78,
                  boxShadow: 'inset 0 0 4px rgba(26, 58, 92, 0.15)',
                }}
              >
                <div
                  className={`${compact ? 'w-[62px] h-[62px]' : 'w-[76px] h-[76px]'} rounded-full flex flex-col items-center justify-center text-center`}
                  style={{ border: '1px solid #1A3A5C', color: '#1A3A5C' }}
                >
                  <p className={`${compact ? 'text-[6px]' : 'text-[7px]'} tracking-widest font-semibold`}>
                    NOTAIRE
                  </p>
                  <div className="w-6 h-px bg-[#1A3A5C] my-1 opacity-60"></div>
                  <p className={`${compact ? 'text-[5px]' : 'text-[6px]'} tracking-wider leading-tight`}>
                    SPÉCIMEN
                  </p>
                  <p className={`${compact ? 'text-[5px]' : 'text-[6px]'} tracking-wider`}>
                    ANONYMISÉ
                  </p>
                  <p className={`${compact ? 'text-[5px]' : 'text-[6px]'} mt-0.5 italic`}>
                    illustration
                  </p>
                </div>
                <div
                  className="absolute pointer-events-none"
                  style={{ top: '-3px', right: '12px', width: '2px', height: '2px', background: '#1A3A5C', borderRadius: '50%', opacity: 0.45 }}
                ></div>
              </div>
              <p className={`${compact ? 'text-[8px]' : 'text-[9px]'} text-[#2C363F] mt-2 italic`}>
                Cachet de l'office
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className={`${compact ? 'mt-4 pt-3' : 'mt-5 pt-4'} border-t border-dashed border-[#1A2530]/15 flex items-center justify-between gap-2`}>
            <p className={`${compact ? 'text-[8px]' : 'text-[9px]'} text-[#2C363F] tracking-wider`} style={{ opacity: 0.78 }}>
              Réf. spécimen
            </p>
            <p className={`${compact ? 'text-[8px]' : 'text-[9px]'} text-[#2C363F] tracking-wider text-right`} style={{ opacity: 0.78 }}>
              Document illustratif — non contractuel
            </p>
          </div>
        </div>

        {/* Bottom shadow */}
        <div className="absolute bottom-0 left-0 right-0 h-8 pointer-events-none" style={{ background: 'linear-gradient(to bottom, transparent 30%, rgba(40, 25, 10, 0.05) 80%, rgba(40, 25, 10, 0.08) 100%)' }}></div>
        <div className="absolute top-0 left-0 right-0 h-4 pointer-events-none" style={{ background: 'linear-gradient(to top, transparent 0%, rgba(40, 25, 10, 0.03) 100%)' }}></div>
      </div>
    </div>
  );
};

export default NotaryDocumentPreview;